import { ICombinedUserData } from 'lib/context';
import { checkIfFreemium } from 'lib/utils/functions';
import { NOTIFICATION_TYPES } from './types';
import { TFunction } from 'i18next';

export const getNotificationFilterOptions = (
  userData: ICombinedUserData,
  isCdkUser: boolean,
  t: TFunction
) => {
  const isFreemium = checkIfFreemium(userData);

  const vdpField = userData.isVdpAllowed
    ? [
        {
          value: NOTIFICATION_TYPES.PUSHED_TO_VDP,
          label: t('Video Pushed to VDP'),
        },
      ]
    : [];

  const salesField = userData.isAutomotiveSalesRole
    ? [
        {
          value: NOTIFICATION_TYPES.SALES,
          label: t('Test Drive Schedule'),
        },
      ]
    : [];

  const serviceField = userData.isAutomotiveServiceRole
    ? [
        {
          value: NOTIFICATION_TYPES.VIDEO_REQUEST,
          label: t('Video Requests / Service Videos'),
        },
        {
          value: NOTIFICATION_TYPES.QUOTES,
          label: t('Quote Updates'),
        },
      ]
    : [];

  const videoDeletesField =
    userData.customer.videoDeletionAccess === '1'
      ? [
          {
            value: NOTIFICATION_TYPES.VIDEO_DELETE,
            label: t('Video Delete'),
          },
        ]
      : [];

  if (isFreemium) {
    return [
      {
        value: 'ALL',
        label: t('All notifications'),
      },
      {
        value: NOTIFICATION_TYPES.EMAIL_NOT_DELIVERED,
        label: t('Email not Delivered'),
      },
      {
        value: NOTIFICATION_TYPES.VIEW,
        label: t('Video Viewed'),
      },
    ];
  }

  return [
    {
      value: 'ALL',
      label: t('All notifications'),
    },
    {
      value: NOTIFICATION_TYPES.EMAIL_NOT_DELIVERED,
      label: t('Email not Delivered'),
    },
    {
      value: NOTIFICATION_TYPES.EMAIL_OPENED,
      label: t('Email Opened'),
    },
    {
      value: NOTIFICATION_TYPES.VIEW,
      label: t('Video Viewed'),
    },
    {
      value: NOTIFICATION_TYPES.CTA,
      label: t('CTA Clicked'),
    },
    {
      value: NOTIFICATION_TYPES.REPLY,
      label: t('Video Replies'),
    },
    ...(isCdkUser
      ? []
      : [
          {
            value: NOTIFICATION_TYPES.REACTIONS_COMMENTS,
            label: t('Comments and Reactions'),
          },
        ]),
    ...serviceField,
    ...vdpField,
    ...salesField,
    ...(isCdkUser
      ? []
      : [
          {
            value: NOTIFICATION_TYPES.FILES,
            label: t('File Shared'),
          },
        ]),
    ...(isCdkUser
      ? []
      : [
          {
            value: NOTIFICATION_TYPES.BOARDS,
            label: t('Board Shared'),
          },
        ]),
    {
      value: NOTIFICATION_TYPES.MESSAGES,
      label: t('Messages'),
    },
    ...videoDeletesField,
    {
      value: NOTIFICATION_TYPES.REPORTS,
      label: t('Reports'),
    },
  ];
};

export const NOTIFICATION_EMAILS = {
  FILES: 'filesEmail',
  BOARDS: 'boardsEmail',
  QUOTE_UPDATE: 'quoteUpdateEmail',
  REACTIONS: 'reactionsEmail',
  TEST_DRIVE: 'testDriveScheduleEmail',
  VDP: 'pushedToVdpEmail',
  CTAS: 'ctaClicksEmail',
  VIDEO_REPLY: 'videoReplyEmail',
  VIDEO_VIEW: 'videoViewsEmail',
  EMAIL_STATUS: 'videoEmailDeliveryStatusEmail',
  LIVE: 'liveEmail',
  EMAIL_OPEN_STATUS: 'videoEmailOpenStatusEmail',
  VIDEO_REQUEST: 'videoRequestEmail',
  VIDEO_APPROVAL_UPDATES: 'videoApprovalUpdatesEmail',
};
export const NOTIFICATION_IN_APP = {
  FILES: 'filesInApp',
  BOARDS: 'boardsInApp',
  QUOTE_UPDATE: 'quoteUpdateInApp',
  TEST_DRIVE: 'testDriveScheduleInApp',
  REACTIONS: 'reactionsInApp',
  VDP: 'pushedToVdpInApp',
  CTAS: 'ctaClicksInApp',
  VIDEO_REPLY: 'videoReplyInApp',
  VIDEO_VIEW: 'videoViewsInApp',
  EMAIL_STATUS: 'videoEmailDeliveryStatusInApp',
  LIVE: 'liveInApp',
  EMAIL_OPEN_STATUS: 'videoEmailOpenStatusInApp',
  VIDEO_REQUEST: 'videoRequestInApp',
  VIDEO_APPROVAL_UPDATES: 'videoApprovalUpdatesInApp',
};

export const getNotificationCategories = (t: TFunction) => ({
  VIDEO: {
    categoryName: t('Videos'),
    isLead: false,
    notifications: [
      {
        id: 2,
        name: t('Email not Delivered'),
        valueEmail: NOTIFICATION_EMAILS.EMAIL_STATUS,
        valueInApp: NOTIFICATION_IN_APP.EMAIL_STATUS,
      },
      {
        id: 16,
        name: t('Email Opened'),
        valueEmail: NOTIFICATION_EMAILS.EMAIL_OPEN_STATUS,
        valueInApp: NOTIFICATION_IN_APP.EMAIL_OPEN_STATUS,
      },
      {
        id: 3,
        name: t('Video Viewed'),
        valueEmail: NOTIFICATION_EMAILS.VIDEO_VIEW,
        valueInApp: NOTIFICATION_IN_APP.VIDEO_VIEW,
      },
      {
        id: 4,
        name: t('CTA Clicked'),
        valueEmail: NOTIFICATION_EMAILS.CTAS,
        valueInApp: NOTIFICATION_IN_APP.CTAS,
      },
      {
        id: 9,
        name: t('Video Replies'),
        valueEmail: NOTIFICATION_EMAILS.VIDEO_REPLY,
        valueInApp: NOTIFICATION_IN_APP.VIDEO_REPLY,
      },
      {
        id: 10,
        name: t('Comments and Reactions'),
        valueEmail: NOTIFICATION_EMAILS.REACTIONS,
        valueInApp: NOTIFICATION_IN_APP.REACTIONS,
      },
    ],
  },
  SERVICE: {
    categoryName: t('Service'),
    isLead: false,
    notifications: [
      {
        id: 17,
        name: t('Video Requests / Service Videos'),
        valueEmail: NOTIFICATION_EMAILS.VIDEO_REQUEST,
        valueInApp: NOTIFICATION_IN_APP.VIDEO_REQUEST,
      },
      {
        id: 4,
        name: t('Quote Updates'),
        valueEmail: NOTIFICATION_EMAILS.QUOTE_UPDATE,
        valueInApp: NOTIFICATION_IN_APP.QUOTE_UPDATE,
      },
      {
        id: 18,
        name: t('Video Approval Updates'),
        valueEmail: NOTIFICATION_EMAILS.VIDEO_APPROVAL_UPDATES,
        valueInApp: NOTIFICATION_IN_APP.VIDEO_APPROVAL_UPDATES,
      },
    ],
  },
  SALES: {
    categoryName: t('Sales'),
    isLead: false,
    notifications: [
      {
        id: 11,
        name: t('Pushed To VDP'),
        valueEmail: NOTIFICATION_EMAILS.VDP,
        valueInApp: NOTIFICATION_IN_APP.VDP,
      },
      {
        id: 5,
        name: t('Test Drive Schedule'),
        valueEmail: NOTIFICATION_EMAILS.TEST_DRIVE,
        valueInApp: NOTIFICATION_IN_APP.TEST_DRIVE,
      },
    ],
  },
  FILE_AND_BOARDS: {
    categoryName: t('Files and Boards'),
    isLead: false,
    notifications: [
      {
        id: 5,
        name: t('File Shared'),
        valueEmail: NOTIFICATION_EMAILS.FILES,
        valueInApp: NOTIFICATION_IN_APP.FILES,
      },
      {
        id: 6,
        name: t('Board Shared'),
        valueEmail: NOTIFICATION_EMAILS.BOARDS,
        valueInApp: NOTIFICATION_IN_APP.BOARDS,
      },
    ],
  },
});

export const getNotificationCategoriesWithNotifications = (
  userData: ICombinedUserData,
  isCdkUser: boolean,
  t: TFunction
) => {
  const isFreemium = checkIfFreemium(userData);
  const { isAutomotive, isAutomotiveSalesRole, isAutomotiveServiceRole } =
    userData;

  const NOTIFICATION_CATEGORIES = getNotificationCategories(t);

  if (isFreemium) {
    const category = { ...NOTIFICATION_CATEGORIES.VIDEO };
    const notifications = category.notifications.filter(item => {
      return item.id !== 4 && item.id !== 9 && item.id !== 10;
    });
    category.notifications = notifications;
    return [{ ...category }];
  }

  if (isCdkUser) {
    const filteredVideo = {
      ...NOTIFICATION_CATEGORIES.VIDEO,
      notifications: NOTIFICATION_CATEGORIES.VIDEO.notifications.filter(n =>
        isCdkUser ? n.id !== 10 : true
      ),
    };

    const filteredSales = {
      ...NOTIFICATION_CATEGORIES.SALES,
      notifications: NOTIFICATION_CATEGORIES.SALES.notifications.filter(
        n => n.id !== 11
      ),
    };
    return [
      filteredVideo,
      ...(isAutomotive && isAutomotiveSalesRole ? [filteredSales] : []),
    ];
  }

  if (isAutomotive && isAutomotiveServiceRole) {
    return [
      NOTIFICATION_CATEGORIES.VIDEO,
      NOTIFICATION_CATEGORIES.SERVICE,
      NOTIFICATION_CATEGORIES.FILE_AND_BOARDS,
    ];
  }

  if (isAutomotive && isAutomotiveSalesRole) {
    const vdpField = userData.isVdpAllowed;
    const salesField = { ...NOTIFICATION_CATEGORIES.SALES };
    if (!vdpField) {
      salesField.notifications = salesField.notifications.filter(
        notification => notification.id !== 11
      );
    }
    return [
      NOTIFICATION_CATEGORIES.VIDEO,
      salesField,
      NOTIFICATION_CATEGORIES.FILE_AND_BOARDS,
    ];
  }

  return [
    NOTIFICATION_CATEGORIES.VIDEO,
    NOTIFICATION_CATEGORIES.FILE_AND_BOARDS,
  ];
};
